import type { Disposable } from './disposable';
import { injectable, inject } from '../shared/inversify';
import { ErrorHandlerRegistry } from './error-handler-contribution';
import { Logger } from './logger';
import { createDisposableListener } from '../utils/event-listener';

type Handler = (error: Error) => boolean;

export const ErrorHandler = Symbol.for('ErrorHandler');

export interface ErrorHandler extends Disposable {
    onStart();
    addHandler(handler: Handler): Disposable;
}

/**
 * Error handler implementation that handles and logs errors.
 */
@injectable()
export class ErrorHandlerImpl implements Disposable {
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(ErrorHandlerRegistry)
        private readonly _errorHandlerRegistry: ErrorHandlerRegistry,
    ) {

    }

    private _disposes: Disposable[] = [];

    /**
     * Starts the error handling process by attaching event listeners.
     */
    onStart() {
        this._disposes.push(createDisposableListener(window, 'error', this._handleError.bind(this)));
        this._disposes.push(createDisposableListener(window, 'unhandledrejection', this._handleRejectionError.bind(this)));
    }

    /**
     * Executes the registered error handlers for the given error.
     * @param error - The error to be handled.
     * @returns True if any handler successfully handles the error, false otherwise.
     */
    private _executeHandlers(error: Error): boolean {
        for(let handler of this._errorHandlerRegistry.handlers) {
            let bool = false;
            try {
                bool = handler(error);
            } catch (e) {
                this._logger.error('Error when executing handler', e, handler);
            }
            if (bool) {
                return true;
            }
        }
        return false;
    }

    /**
     * Event handler for the 'error' event.
     * Prevents the default behavior and stops the event propagation.
     * @param event - The error event.
     */
    private _handleError (event: Event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        //@ts-ignore
        this._executeHandlers(event);
    }

    /**
     * Event handler for the 'unhandledrejection' event.
     * Prevents the default behavior and stops the event propagation.
     * @param e - The unhandled rejection event.
     */
    private _handleRejectionError(e: Event) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const event = e as PromiseRejectionEvent;
        const error = event.reason;
        this._executeHandlers(error);
    }

    /**
     * Disposes the error handler by disposing all attached event listeners.
     */
    dispose(): void {
        this._disposes.forEach(disposable => {
            disposable.dispose();
        });
    }
}
